import React from "react";
import { Draggable } from "react-beautiful-dnd";
import {
  ComponentEnum,
  ComponentI,
  isSupportedComponentType,
} from "../../../types";
import combineClassName from "../../../componentLibrary/utils/combineClassName";
import { SelectedComponentType } from "../utils/selectedComponentUtils";
import { ComponentGroup } from "../utils/componentGroup";
import { GroupedComponents } from "./GroupedComponents";

export const renderContentCard = (
  component: ComponentI,
  handleContentChange: (
    id: string,
    field: string,
    value: any, //eslint-disable-line @typescript-eslint/no-explicit-any
  ) => void,
  onRemove: () => void,
  provided: any, //eslint-disable-line @typescript-eslint/no-explicit-any
  selectedComponent: SelectedComponentType,
  handleComponentClick: (selectedComponent: SelectedComponentType) => void,
  variableUptoCurrentPage: string[],
  componentRefs: React.RefObject<{ [key: string]: HTMLDivElement | null }>,
  handleTypeChange: (componentId: string, value: ComponentEnum) => void,
  isMinimized: boolean,
  setIsMinimized: (isMinimized: boolean) => void,
  handleTitleClick?: () => void,
  componentGroup?: ComponentGroup | null,
) => {
  switch (component.type) {
    case ComponentEnum.INPUT:
    case ComponentEnum.MULTIPLE_CHOICE:
    case ComponentEnum.USER_LIST:
    case ComponentEnum.PRIORITY_LIST:
    case ComponentEnum.RATING_SCALE:
    case ComponentEnum.VIDEO:
    case ComponentEnum.TEXT:
    case ComponentEnum.TOGGLE_TEXT:
    case ComponentEnum.IMAGE:
    case ComponentEnum.ACTION_DETAIL:
    case ComponentEnum.CUSTOM_LINK:
    case ComponentEnum.LESSON_LINK:
    case ComponentEnum.LOGIC_COMPONENT:
    case ComponentEnum.DATE_TIME:
    case ComponentEnum.CALLOUT_BOX:
    case ComponentEnum.FOR_EACH:
    case ComponentEnum.TOGGLE:
    case ComponentEnum.ADD_TO_CALENDAR:
      // If component has a group, render it with GroupedComponents
      if (componentGroup) {
        return (
          <GroupedComponents
            groupTitle={componentGroup.title}
            groups={componentGroup.components}
            component={component}
            handleContentChange={handleContentChange}
            onRemove={onRemove}
            provided={provided}
            selectedComponent={selectedComponent}
            handleComponentClick={handleComponentClick}
            variableUptoCurrentPage={variableUptoCurrentPage}
            componentRefs={componentRefs}
            handleTitleClick={handleTitleClick}
            handleTypeChange={handleTypeChange}
            isMinimized={isMinimized}
            setIsMinimized={setIsMinimized}
          />
        );
      }
      // eslint-disable-next-line no-console
      console.warn(
        `Component type ${component.type} should have a group configuration`,
      );
      return null;
    default: {
      // DB may contain component types from other envs (e.g. stage-only); ignore unsupported types to avoid asset/load errors
      if (!isSupportedComponentType(component.type)) {
        return null;
      }
      const _exhaustiveCheck: never = component.type;
      throw new Error(`Unhandled component type: ${_exhaustiveCheck}`);
    }
  }
};

const DraggableComponentCard = ({
  component,
  index,
  handleContentChange,
  onRemove,
  variableUptoCurrentPage,
  selectedComponent,
  handleComponentClick,
  parentComponent,
  componentRefs,
  handleTypeChange,
  componentGroup,
  isMinimized,
  setIsMinimized,
}: {
  component: ComponentI;
  index: number;
  handleContentChange: (
    id: string,
    field: string,
    value: string | boolean,
  ) => void;
  onRemove: () => void;
  variableUptoCurrentPage: string[];
  selectedComponent: SelectedComponentType;
  handleComponentClick: (selectedComponent: SelectedComponentType) => void;
  parentComponent?: ComponentI;
  componentRefs: React.RefObject<{ [key: string]: HTMLDivElement | null }>;
  handleTypeChange: (componentId: string, value: ComponentEnum) => void;
  isMinimized: boolean;
  setIsMinimized: (isMinimized: boolean) => void;
  componentGroup?: ComponentGroup | null;
}) => {
  const handleTitleClick = () => {
    handleComponentClick({
      component: component,
      parentComponent: parentComponent,
    });
  };
  return (
    <Draggable draggableId={component._id} index={index}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={provided.draggableProps.style}
          className={combineClassName([
            selectedComponent.component?._id === component._id
              ? "border-2 border-electrum-300 rounded-md"
              : "",
          ])}
        >
          {renderContentCard(
            component,
            handleContentChange,
            onRemove,
            provided,
            selectedComponent,
            handleComponentClick,
            variableUptoCurrentPage,
            componentRefs,
            handleTypeChange,
            isMinimized,
            setIsMinimized,
            handleTitleClick,
            componentGroup,
          )}
        </div>
      )}
    </Draggable>
  );
};

export default DraggableComponentCard;
